import classNames from 'classnames'
import moment from 'moment'
import React from 'react'
import { Column, useTable, useSortBy, usePagination } from 'react-table'
import ContextMenu from 'src/components/ui/ContextMenu'
import { IActiveTripResponseV2, IOrgCreateObj, IRecentTripObj, ITripFilters, ITripRequestObject } from 'src/types'
import UIMessages from 'src/utils/ui-messages'
import ReactTablePagination from '../../../ui/ReactTablePagination'
import history from '../../../../history'
import { Link } from 'react-router-dom'
import Edit from 'src/components/icons/ui/Edit'
import { TripStatuses } from 'src/constants/trip-statuses'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import HorizontalSelector from 'src/components/HorizontalSelector'
import HorizontalSelectorItem from 'src/components/HorizontalSelectorItem'
import Modal from 'src/components/ui/Modal'
import { Button } from '@mobiscroll/react-latest'
import { MoonLoader } from 'react-spinners'
// import NoData from '../../../../../public/images/no_data.gif'

const PAGE_SIZE = 10

interface IProps<T extends object> {
  columns: Array<Column<T>>
  // filters: IPromotion
  data: T[]
  loading: boolean
  pageCount: number
  pageSize: number
  fetchData: (params: { pageIndex: number; pageSize: number }) => void
  updateFilters: (params: any) => Promise<any>
}
function Table<T extends object>({
  columns,
  data,
  fetchData,
  updateFilters,
  // filters,
  pageSize,
  loading,
  pageCount: controlledPageCount
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { pageIndex, sortBy } = state

  React.useEffect(() => {
    fetchData({ pageIndex, pageSize })
  }, [pageIndex, pageSize])

  return (
    <React.Fragment>
      {rows.length > 0 && (
        <table {...getTableProps()} className='fixed-table'>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    className={classNames('', {
                      sortable: column.canSort,
                      sortedAsc: column.isSortedDesc === false,
                      sortedDesc: column.isSortedDesc === true,
                      w20: column.width === '20',
                      w50: column.width === '50',
                      w150: column.width === '150',
                      pr2: column.width !== '20'
                    })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading ? (
              <tr>
                <td>Loading...</td>
              </tr>
            ) : (
              rows.map(
                (row, i) =>
                  +prepareRow(row) || (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell, index) => {
                        return (
                          <td {...cell.getCellProps()} key={`Recurring-${index}`}>
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  )
              )
            )}
          </tbody>
        </table>
      )}
      {rows.length === 0 && loading === false && (
        <div className='p2 flex items-center justify-center h5 font-grey2'>
          <img width={130} src='/images/no_data.gif' alt='No data availabe.' />
        </div>
      )}
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

interface IReccuuringTripsPaneProps {
  riderId: string
  orgId: string
  organizations: IOrgCreateObj | null
  recurringTripsItems: any[]
  recurringTripsCount: number
  destroyedRecurringTaskId: string
  fetchTrips: (params: ITripFilters) => Promise<any>
  getRecuringTripByRiderId: (riderId: string) => Promise<any>
  destroyRecurringTripTask: (recurrinTripId: string) => void
  cloneTrip: (trip: IActiveTripResponseV2) => Promise<any>
  updateRecurringTripFilters: (params: any) => Promise<any> // not implemented yet for future reference
}
export const RecurringTripsPane = (props: IReccuuringTripsPaneProps) => {
  const [loading, setLoading] = React.useState(true)
  const [isActive, setIsActive] = React.useState(true)
  const [pageCount, setPageCount] = React.useState(0)
  const [pageSize] = React.useState(PAGE_SIZE)
  const [upcomingTrips, setUpcomingTrips] = React.useState([])
  const [timeFormat, setTimeFormat] = React.useState('M/D/YYYY hh:mmA')

  const [modalState, setModalState] = React.useState(false)
  const [modalTitle, setModalTitle] = React.useState('')
  const [action, setAction] = React.useState<string | null>(null)
  const [selectedActiveTrip, setSelectedActiveTrip] = React.useState<string | IActiveTripResponseV2 | null>(null)

  const [selectedTab, setSelectedTab] = React.useState<'recurring' | 'upcoming'>('recurring')
  const columns = React.useMemo(
    () => [
      {
        id: 'pickupLocation',
        Header: 'Pickup',
        width: '200px',
        accessor: (recurringTrip: ITripRequestObject) => {
          return <div>{recurringTrip.pickupLocation.fullAddress}</div>
        }
      },
      {
        id: 'dropoffLocation',
        Header: 'Dropoff',
        width: '200px',
        accessor: (recurringTrip: ITripRequestObject) => {
          return <div>{recurringTrip.dropoffLocation.fullAddress}</div>
        }
      },
      {
        id: 'pickupTime',
        Header: 'Pickup Time',
        width: '80px',
        accessor: (recurringTrip: ITripRequestObject) => {
          const value = formatDate(recurringTrip.recurringPickupTime, timeFormat)
          return <div>{value}</div>
        }
      },
      {
        id: 'dropOffTime',
        Header: 'Dropoff Time',
        width: '80px',
        accessor: (recurringTrip: ITripRequestObject) => {
          const value = formatDate(recurringTrip.recurringDropoffTime, timeFormat)
          return <div>{value}</div>
        }
      },
      {
        id: 'crontabExp',
        Header: 'Scheduled Days',
        accessor: (recurringTrip: ITripRequestObject) => {
          const days = displayRecurringDays(recurringTrip.crontabExp)
          return <div>{days}</div>
        }
      },
      {
        id: 'timesToRecur',
        Header: 'Ends',
        width: '100px',
        accessor: (recurringTrip: ITripRequestObject) => {
          if (recurringTrip.runForever === false) {
            return <div>{moment(recurringTrip.endDate).format('MMM.DD,Y')}</div>
          }
          return <div>{UIMessages.trip.recurringTrips.doesntEnd}</div>
        },
        className: 'flex items-center'
      },
      {
        id: 'notes',
        Header: 'Notes',
        accessor: 'notes',
        className: 'flex items-center'
      },
      {
        id: 'buttons',
        Header: '',
        width: '20',
        accessor: (recurringTrip: IActiveTripResponseV2) => {
          return <ButtonsMenus recurringTrip={recurringTrip} />
        }
      }
    ],
    [timeFormat]
  )

  const upcomingTripsColumns = React.useMemo(
    () => [
      {
        Header: 'Pickup',
        id: 'pickup',
        // width: '200px',
        accessor: ({ pickupLocation }: any) => pickupLocation.fullAddress,
        className: 'flex items-center',
        disableSortBy: true,
        Cell: (data: any) => {
          const { pickupLocation, id } = data.row.original
          return pickupLocation.fullAddress
        }
      },
      {
        Header: 'Dropoff',
        id: 'dropoff',
        // width: '200px',
        accessor: ({ dropoffLocation }: any) => {
          return dropoffLocation.fullAddress
        },
        className: 'flex items-center',
        disableSortBy: true,
        Cell: (data: any) => {
          const { dropoffLocation, id } = data.row.original
          return dropoffLocation.fullAddress
        }
      },
      {
        Header: 'Scheduled Pickup',
        id: 'scheduledPickupTime',
        // width: '120px',
        accessor: ({ scheduledPickupTime, id }: any) => {
          return scheduledPickupTime ? moment(scheduledPickupTime).format(timeFormat) : ''
        },
        className: 'flex items-center'
      },
      {
        Header: 'Scheduled Dropoff',
        id: 'scheduledDropoffTime',
        // width: '120px',
        accessor: ({ scheduledDropoffTime, id }: any) => {
          return scheduledDropoffTime ? moment(scheduledDropoffTime).format(timeFormat) : ''
        },
        className: 'flex items-center'
      },
      {
        Header: 'Operator Info',
        id: 'operator',
        accessor: 'operator',
        className: 'flex items-center',
        Cell: (data: any) => {
          const { operator, id } = data.row.original
          const operatorName = operator ? `${operator.firstName} ${operator.lastName}` : 'No operator assigned'
          const operatorId = operator && operator.operatorId ? operator.operatorId : operatorName
          return <CellBasicInfo name={`${operatorName}`} id={operatorId} tripId={id} />
        }
      },
      {
        Header: 'Created At',
        id: 'created',
        // width: '120px',
        accessor: ({ createdAt, id }: any) => {
          return moment(createdAt).format(timeFormat)
        },
        className: 'flex items-center'
      },
      {
        Header: 'Vehicle',
        id: 'vehicle',
        // width: '120px',
        accessor: 'vehicle',
        className: 'flex items-center',
        disableSortBy: true,
        Cell: (data: any) => {
          const { vehicle, id } = data.row.original
          if (vehicle) {
            return (
              <span className='font-black font-small bold'>{`${vehicle.make} ${vehicle.model} ${
                vehicle.licensePlate
              }`}</span>
            )
          }
          return <span className='font-black font-small bold'>No vehicle</span>
        }
      },
      {
        Header: 'Product',
        id: 'product',
        // width: '120px',
        accessor: ({ product }: any) => product.name,
        className: 'flex items-center',
        Cell: (data: any) => {
          const { product, id } = data.row.original
          return product.name
        }
      },
      {
        Header: 'Count',
        id: 'riderCount',
        width: '50',
        accessor: 'rider',
        className: 'flex items-center',
        Cell: (data: any) => {
          const { riderCount } = data.row.original
          return <div>{riderCount}</div>
        }
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: 'status',
        width: '150',
        Cell: (data: any) => {
          const trip = data.row.original
          const tripStatus = trip.lastStatus
          const statusTime = moment(trip.lastStatusAt).format(timeFormat)
          return (
            <div>
              <p className='m0 p0 font-blue font-small bold'>{tripStatus}</p>
              <p className='m0 p0 font-small bold font-black'> {statusTime}</p>
            </div>
          )
        }
      },
      {
        Header: '',
        id: 'context-menu',
        accessor: '',
        className: '',
        disableSortBy: true,
        width: '20',
        Cell: (data: any) => {
          return <CellContextMenu trip={data.row.original} />
        }
      }
    ],
    [timeFormat]
  )

  const CellBasicInfo = (props: { name: string; id: string; tripId: string }) => (
    <div className='flex items-center'>
      <Link to={`/trips/${props.tripId}`}>
        <div className='ml1 TeamMemberName font-dark-grey'>{props.name}</div>
      </Link>
    </div>
  )

  const CellContextMenu = (props: { trip: IRecentTripObj }) => {
    const scheduledStatuses = new Set([
      TripStatuses.SCHEDULED,
      TripStatuses.SCHEDULE_REQUESTED,
      TripStatuses.SCHEDULE_ACCEPTED
    ])
    const lastStatus = props.trip.lastStatus
    const isScheduled = scheduledStatuses.has(lastStatus)
    const handleClick = (e: any) => {
      e.stopPropagation()
    }
    return (
      <div className='flex items-center'>
        <ContextMenu>
          <div
            onClick={handleClick}
            className={classNames('MenuItem flex', {
              'cursor--pointer': isScheduled,
              'cursor--not-allowed': !isScheduled
            })}
          >
            <Link to={`/trips-management/${props.trip.id}/assign-operator`}>
              <Edit className='icon--small svg-stroke-grey3 mr1' />
              <span className='font-dark-grey'>Assign</span>
            </Link>
          </div>
          <div
            onClick={handleClick}
            className={classNames('MenuItem flex', {
              'cursor--pointer': isScheduled,
              'cursor--not-allowed': !isScheduled
            })}
          >
            <Link to={`/trips-management/${props.trip.id}/cancel-manually`}>
              <Edit className='icon--small svg-stroke-grey3 mr1' />
              <span className='font-dark-grey'>Cancel</span>
            </Link>
          </div>
        </ContextMenu>
      </div>
    )
  }
  React.useEffect(() => {
    setPageCount(Math.ceil(props.recurringTripsCount / pageSize))
  }, [props.recurringTripsCount])
  React.useEffect(() => {
    fetchData({ pageIndex: 0, pageSize: PAGE_SIZE })
  }, [props.destroyedRecurringTaskId])
  React.useEffect(() => {
    fetchUpcomingTrips()
  }, [props.riderId])
  React.useEffect(() => {
    const orgTimeFormat = props.organizations ? props.organizations.timeFormat : null
    const timeFormat = orgTimeFormat === 'mil' ? 'M/D/YYYY HH:mm' : 'M/D/YYYY hh:mmA'
    setTimeFormat(timeFormat)
  }, [props.organizations])
  const ConfirmationModal = (props: { submitAction: (isConfirmed: boolean) => void }) => {
    return (
      <Modal>
        <div className='fixed top-0 bottom-0 left-0 right-0 flex justify-center items-center'>
          <div className='med-modal'>
            <span className='ModalAddTitle font-small weight-300 font-grey2 uppercase'>
              {UIMessages.common.confirmationMessage}
            </span>
            <div className='mt1 border-top'>
              <div className='h5 mt1'>You are about to {modalTitle} this trip. Please confirm the action.</div>
            </div>
            <div className='flex items-center justify-end mt3'>
              <button
                type='button'
                aria-label='cancel'
                className='custom-btn mbsc-clear-btn mr2'
                onClick={() => props.submitAction(false)}
              >
                {UIMessages.common.cancel}
              </button>
              <button
                type='button'
                aria-label='confirm'
                className='custom-btn mbsc-green-btn'
                onClick={() => props.submitAction(true)}
              >
                {'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  const ButtonsMenus = (props: { recurringTrip: IActiveTripResponseV2 }) => {
    return (
      <ContextMenu>
        <div className='onDeactivateOption' onClick={() => showConfirmation('deactivate', props.recurringTrip.id)}>
          <span className='font-dark-grey h5 pr1 pl1 w100'>{UIMessages.common.deactivate}</span>
        </div>
        <div className='onDeactivateOption' onClick={() => showConfirmation('update', props.recurringTrip)}>
          <span className='font-dark-grey h5 pr1 pl1 w100'>{UIMessages.common.update}</span>
        </div>
      </ContextMenu>
    )
  }

  const showConfirmation = (stateAction: string, selected: any) => {
    setModalTitle(stateAction)
    setAction(stateAction)
    setModalState(!modalState)
    setSelectedActiveTrip(selected)
  }
  const confirmAction = (isConfirmed: boolean) => {
    if (isConfirmed) {
      action === 'deactivate'
        ? destroyCronTask(selectedActiveTrip as string)
        : editRecurringTrip(selectedActiveTrip as IActiveTripResponseV2)
    }
    resetState()
  }
  const resetState = () => {
    setModalTitle('')
    setModalState(false)
    setAction(null)
    setSelectedActiveTrip(null)
  }

  const destroyCronTask = (id: string) => {
    props.destroyRecurringTripTask(id)
  }

  const editRecurringTrip = (trip: IActiveTripResponseV2) => {
    const recurringTrip: IActiveTripResponseV2 = {
      ...trip,
      isUpdatedRecurringTrip: true
    }
    history.push(`/trips/create/${recurringTrip.rider.riderId}`)
    props.cloneTrip(recurringTrip)
  }

  const fetchData = async ({ pageSize, pageIndex }: { pageSize: number; pageIndex: number }) => {
    const res = await props.getRecuringTripByRiderId(props.riderId)
    setLoading(false)
  }
  const displayRecurringDays = (crontabExp: string | undefined) => {
    if (crontabExp) {
      const crontabArr = crontabExp ? crontabExp.split('*') : []
      const days = crontabArr[crontabArr.length - 1].split(',').join(', ')
      return days
    }
    return ''
  }
  const formatDate = (date: string | undefined, timeFormat: string) => {
    if (date) {
      const hourMinFormat = timeFormat.split(' ')
      return moment(date).format(hourMinFormat[1])
    }
    return ''
  }

  const fetchUpcomingTrips = async () => {
    const data = await props.fetchTrips({
      orgId: props.orgId,
      timeField: 'pickup',
      timeStart: moment()
        .add(1, 'day')
        .startOf('day')
        .toISOString(),
      timeEnd: moment()
        .add(3, 'week') // extend displaying of upcoming trip data
        .endOf('day')
        .toISOString(),
      riderId: [props.riderId]
    })
    if (data && data.items) {
      setUpcomingTrips(data.items)
    }
    setLoading(false)
  }
  return (
    <FloatingForm fullHeight={true}>
      <div className='react-table w100'>
        {modalState && <ConfirmationModal submitAction={isConfirmed => confirmAction(isConfirmed)} />}
        <div className='font-grey2 bold h5 mb1'>{UIMessages.rider.recurringTrips.recurringRides}</div>
        {loading === true ? (
          <div className='p3 m3 flex items-center justify-center h5 font-grey2'>
            <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
          </div>
        ) : (
          <div className='mt1 border-top w100'>
            <Table
              columns={columns}
              data={props.recurringTripsItems}
              fetchData={fetchData}
              loading={loading}
              pageCount={pageCount}
              pageSize={pageSize}
              updateFilters={props.updateRecurringTripFilters}
            />
          </div>
        )}
        <div className='bb-shadow-grey'>&nbsp;</div>
        <div className='font-grey2 bold h5 mb1 mt3'>{UIMessages.rider.recurringTrips.upcomingTrips}</div>
        {loading === true ? (
          <div className='p3 m3 flex items-center justify-center h5 font-grey2'>
            <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
          </div>
        ) : (
          <div className='mt1 border-top w100'>
            <Table
              columns={upcomingTripsColumns}
              data={upcomingTrips}
              fetchData={fetchUpcomingTrips}
              loading={loading}
              pageCount={pageCount}
              pageSize={pageSize}
              updateFilters={props.updateRecurringTripFilters}
            />
          </div>
        )}
      </div>
    </FloatingForm>
  )
}



// WEBPACK FOOTER //
// ./src/components/riders/riderdetails/recurringTrips/RecurringTripsPane.tsx